/**
 * useMediaUpload Hook
 *
 * Handles file uploads for collection media fields (images and videos).
 * Uses the /api/upload/media endpoint for R2 storage.
 */

import { useState, useCallback } from 'react';

// ============================================================
// TYPES
// ============================================================

export interface UploadResult {
  success: boolean;
  key: string;
  url: string;
  type: 'image' | 'video';
  filename: string;
  originalName: string;
  size: number;
  contentType: string;
}

interface UseMediaUploadReturn {
  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Upload progress (0-100) - approximate since we can't track R2 progress */
  progress: number;
  /** Error message if upload failed */
  error: string | null;
  /** Upload a file and return the URL */
  uploadFile: (file: File) => Promise<UploadResult | null>;
  /** Clear any error state */
  clearError: () => void;
}

// ============================================================
// CONSTANTS
// ============================================================

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB

// ============================================================
// HOOK IMPLEMENTATION
// ============================================================

export function useMediaUpload(): UseMediaUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const uploadFile = useCallback(async (file: File): Promise<UploadResult | null> => {
    setError(null);
    setProgress(0);

    // Validate file type
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

    if (!isImage && !isVideo) {
      setError(`Invalid file type. Allowed: JPEG, PNG, GIF, WebP, MP4, WebM, MOV`);
      return null;
    }

    // Validate file size
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
      const maxMB = maxSize / 1024 / 1024;
      const fileMB = (file.size / 1024 / 1024).toFixed(1);
      setError(`File too large: ${fileMB}MB. Maximum: ${maxMB}MB`);
      return null;
    }

    setIsUploading(true);
    setProgress(10); // Show some initial progress

    try {
      const formData = new FormData();
      formData.append('file', file);

      setProgress(30);

      const response = await fetch('/api/upload/media', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      setProgress(80);

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      setProgress(100);

      return {
        success: true,
        key: result.key,
        url: result.url,
        type: result.type,
        filename: result.filename,
        originalName: result.originalName,
        size: result.size,
        contentType: result.contentType,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    isUploading,
    progress,
    error,
    uploadFile,
    clearError,
  };
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Check if a file is a valid image for upload
 */
export function isValidImageFile(file: File): boolean {
  return ALLOWED_IMAGE_TYPES.includes(file.type);
}

/**
 * Check if a file is a valid video for upload
 */
export function isValidVideoFile(file: File): boolean {
  return ALLOWED_VIDEO_TYPES.includes(file.type);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
